import {
  Box,
  LoadingOverlay,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { CollectionStep, type CollectionStepIndex } from 'components/CollectionStep';
import React, { isValidElement, type PropsWithChildren } from 'react';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';
import type { CurrencyCode } from 'types/currency';

export interface KanbanColumnProps
  extends PropsWithChildren<{
    color: string;
    isLoading?: boolean;
    step?: CollectionStepIndex;
    total?: number;
    currency?: CurrencyCode;
  }> {}

export const KanbanColumn = ({
  children,
  color,
  isLoading = false,
  step,
  total,
  currency,
}: KanbanColumnProps) => {
  const theme = useMantineTheme();
  const formatter = useLocalizedCurrencyFormatter(false, currency);
  const arrayChildren = React.Children.toArray(children).filter(isValidElement);
  const cards = arrayChildren.map(child =>
    // @ts-expect-error todo: Fix typing
    React.cloneElement(child, { color }),
  );

  const renderTitle = () => {
    if (step !== undefined && total !== undefined) {
      return (
        <Stack gap="sm">
          <CollectionStep step={step} />
          <Text fw="bold" size="16px" c="gray.7">
            {formatter.format(total)}
          </Text>
        </Stack>
      );
    }

    return <Skeleton width="100%" h="40px" />;
  };

  return (
    <Stack
      gap={10}
      h="100%"
      miw="calc(var(--kanban-min-width) + 20px)"
      style={{ borderRadius: '4px' }}
      bg="gray.2"
      pos="relative"
    >
      <LoadingOverlay
        zIndex={1}
        visible={isLoading}
        overlayProps={{ radius: 'sm', color: theme.colors.gray[4], backgroundOpacity: 0.75 }}
      />
      {/* The explicit height prevents the title box from shrinking alongside the adaptive text */}
      <Box p={10} pb={0} mb="xs" h={62}>
        {renderTitle()}
      </Box>
      <ScrollArea mr={2} scrollHideDelay={0} scrollbarSize={6}>
        {/* This intermediary element provides room for the ScrollArea's scrollbar to display in the gutter without obscuring the content */}
        <Box pl={10} pr={8} pb={16}>
          <Stack gap={8}>{cards}</Stack>
        </Box>
      </ScrollArea>
    </Stack>
  );
};
